import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { CustomTooltipProps, LightTooltipProps } from "./index.types";

const LightTooltip = styled(({ className, tooltipTextSx, ...props }: LightTooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ tooltipTextSx = {} }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "#1b1b1b",
    boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
    ...tooltipTextSx,
  },
}));

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  children,
  tooltipText,
  arrow = false,
  tooltipTextSx = {},
  placement = "top",
  clickToOpen = false,
  sx = {},
  disableInteractive = false,
  enterDelay = 200,
  leaveDelay = 0,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((o) => !o);
    }
  };

  const wrapperStyle = {
    display: "inline-block",
    ...(clickToOpen && !disabled ? { cursor: "pointer" } : {}),
    ...(disabled ? { cursor: "not-allowed", opacity: 0.6 } : {}),
    ...sx,
  };

  const eventHandlers = clickToOpen
    ? {
        onClick: (e: React.MouseEvent) => {
          if (!disabled) {
            e.stopPropagation();
            handleToggle();
          }
        },
      }
    : {
        onMouseEnter: () => !disabled && setOpen(true),
        onMouseLeave: handleClose,
      };

  const tooltip = (
    <LightTooltip
      arrow={arrow}
      placement={placement}
      title={tooltipText}
      open={open}
      onClose={handleClose}
      tooltipTextSx={tooltipTextSx}
      disableHoverListener={clickToOpen}
      disableFocusListener={clickToOpen}
      disableTouchListener={clickToOpen}
      disableInteractive={disableInteractive}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}>
      <span style={wrapperStyle} {...eventHandlers}>
        {children}
      </span>
    </LightTooltip>
  );

  return clickToOpen ? <ClickAwayListener onClickAway={handleClose}>{tooltip}</ClickAwayListener> : tooltip;
};

export default CustomTooltip;
